import * as React from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Button,
  FormHelperText,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import RangeFields from 'components/ReportRequestV2/RangeFields';

import styles from './RangeConstraintList.module.css';

export interface RangeConstraint {
  key: string;
  min: number;
  max: number;
}

export interface RangeConstraintOption {
  value: string;
  label: string;
}

export interface RangeConstraintListProps {
  label: string;
  rows: RangeConstraint[];
  options: RangeConstraintOption[];
  onChange: (rows: RangeConstraint[]) => void;
  bounds: [number, number];
  step?: number;
  unit?: string;
  defaultRange?: [number, number];
  rowErrors?: Record<number, string>;
  error?: string;
  emptyText?: string;
  disabled?: boolean;
}

/** Editable list of `{key, min, max}` constraints with one slider and two number fields per row. */
const RangeConstraintList: React.FC<RangeConstraintListProps> = ({
  label,
  rows,
  options,
  onChange,
  bounds,
  step = 1,
  unit,
  defaultRange,
  rowErrors = {},
  error,
  emptyText = 'No constraints',
  disabled,
}) => {
  const used = new Set(rows.map((r) => r.key));
  const available = options.filter((o) => !used.has(o.value));
  const labelFor = (key: string) => options.find((o) => o.value === key)?.label ?? key;

  const update = (index: number, patch: Partial<RangeConstraint>) =>
    onChange(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const add = () => {
    if (!available.length) return;
    const [min, max] = defaultRange ?? bounds;
    onChange([...rows, { key: available[0].value, min, max }]);
  };

  return (
    <Box className={styles.list} role="group" aria-label={label}>
      {rows.length === 0 && (
        <Typography variant="body2" color="text.secondary" className={styles.empty}>
          {emptyText}
        </Typography>
      )}
      {rows.map((row, index) => {
        const rowLabel = labelFor(row.key);
        const rowError = rowErrors[index];
        return (
          <Box key={`${row.key}-${index}`} className={styles.row}>
            <TextField
              select
              size="small"
              label={`${label} ${index + 1}`}
              value={row.key}
              onChange={(e) => update(index, { key: e.target.value })}
              className={styles.key}
              disabled={disabled}
            >
              {options
                .filter((o) => o.value === row.key || !used.has(o.value))
                .map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
            </TextField>
            <RangeFields
              label={rowLabel}
              min={row.min}
              max={row.max}
              onChange={(range) => update(index, range)}
              bounds={bounds}
              step={step}
              unit={unit}
              error={!!rowError}
              disabled={disabled}
              sliderClassName={styles.slider}
              numberClassName={styles.number}
            />
            <IconButton
              aria-label={`Remove ${rowLabel}`}
              onClick={() => onChange(rows.filter((_, i) => i !== index))}
              disabled={disabled}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
            {rowError && (
              <FormHelperText error className={styles.rowError}>
                {rowError}
              </FormHelperText>
            )}
          </Box>
        );
      })}
      <Box className={styles.footer}>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={add}
          disabled={disabled || available.length === 0}
        >
          Add {label.toLowerCase()}
        </Button>
        {error && <FormHelperText error>{error}</FormHelperText>}
      </Box>
    </Box>
  );
};

export default RangeConstraintList;
